import {
  ListRetrieveGrupo,
  ListRetrieveUsuario,
  PutUsuario,
  PutUsuarioWithRelations,
  UsuariosApiService,
} from '@cei-ufg/ngx-cei-seguranca';
import { formatarCpf } from '../documento-fiscal.util';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { PessoaComUsuario, PessoaUsuarioFormPayload } from '../../services/pessoa-usuario-api.service';
import { PessoaUsuarioFormState } from './pessoa-usuario-form.model';

type UsuarioUpdatePayload = Pick<
  ListRetrieveUsuario,
  'id' | 'login' | 'email' | 'date_joined' | 'is_active' | 'is_staff' | 'user_permissions' | 'groups'
> & {
  password?: string;
};

type UsuarioUpdateWithRelationsPayload = Omit<PutUsuario, 'login' | 'password'> & {
  user_permissions: string[];
  groups: number[];
  password?: string;
};

export function mapUsuarioToPessoaComUsuario(usuario: ListRetrieveUsuario): PessoaComUsuario {
  return {
    id: usuario.id,
    nome: usuario.login,
    cpf: '—',
    usuario,
  } as PessoaComUsuario;
}

export function mapListRetrieveUsuarioToForm(usuario: ListRetrieveUsuario): PessoaUsuarioFormState {
  return {
    nome: '',
    cpf: '',
    login: usuario.login,
    email: usuario.email,
    password: '',
    is_active: usuario.is_active ?? true,
    is_staff: usuario.is_staff ?? false,
    user_permissions: [...usuario.user_permissions],
    groups: [...usuario.groups],
    fotoUrl: null,
  };
}

export function buildUsuarioUpdatePayload(
  form: PessoaUsuarioFormState,
  usuario: ListRetrieveUsuario,
): UsuarioUpdatePayload {
  const payload: UsuarioUpdatePayload = {
    id: usuario.id,
    login: form.login,
    email: form.email,
    date_joined: usuario.date_joined,
    is_active: form.is_active,
    is_staff: form.is_staff,
    user_permissions: form.user_permissions,
    groups: form.groups,
  };

  if (form.password?.trim()) {
    payload.password = form.password;
  }

  return payload;
}

export function buildUsuarioUpdateWithRelationsPayload(
  form: PessoaUsuarioFormState,
  usuario: ListRetrieveUsuario,
): UsuarioUpdateWithRelationsPayload {
  const payload: UsuarioUpdateWithRelationsPayload = {
    id: usuario.id,
    email: form.email,
    date_joined: usuario.date_joined,
    is_active: form.is_active,
    is_staff: form.is_staff,
    user_permissions: form.user_permissions.map((permission) => permission.id),
    groups: form.groups.map((group) => group.id),
  };

  if (form.password?.trim()) {
    payload.password = form.password;
  }

  return payload;
}

/** Monta payload de atualização de permissões/grupos via UsuariosApiService. */
export function montarPutUsuarioWithRelations(
  form: PessoaUsuarioFormState,
  usuario: ListRetrieveUsuario,
): PutUsuarioWithRelations {
  const payload: PutUsuarioWithRelations = {
    id: usuario.id,
    login: usuario.login,
    email: form.email?.trim() || usuario.email,
    date_joined: usuario.date_joined,
    is_active: form.is_active,
    is_staff: form.is_staff,
    user_permissions: form.user_permissions.map((permissao) => Number(permissao.id)),
    groups: form.groups.map((grupo) => grupo.id),
  };

  if (form.password?.trim()) {
    payload.password = form.password;
  }

  return payload;
}

/** Copia permissões e grupos do usuário retornado pela API de autorização para o formulário. */
export function aplicarPermissoesGruposUsuarioNoForm(
  form: PessoaUsuarioFormState,
  usuario: ListRetrieveUsuario,
): void {
  form.user_permissions = [...usuario.user_permissions];
  form.groups = [...usuario.groups];
}

/**
 * Carrega usuário e suas permissões/grupos na API de autorização pelo login.
 * O login é o identificador estável entre pessoa (domínio) e usuário (autorização).
 */
export function carregarUsuarioAutorizacao(
  usuariosApi: UsuariosApiService,
  login: string,
): Observable<ListRetrieveUsuario | null> {
  const loginNormalizado = login.trim();
  if (!loginNormalizado) {
    return of(null);
  }

  return buscarUsuarioIdPorLogin(usuariosApi, loginNormalizado).pipe(
    switchMap((usuarioId) => {
      if (!usuarioId) {
        return of(null);
      }

      return forkJoin({
        usuario: usuariosApi.usuarioRetrieve({ id: usuarioId }),
        permissoesGrupos: usuariosApi.usuarioPermissoesGruposRetrieve({ id: usuarioId }),
      }).pipe(
        map(({ usuario, permissoesGrupos }) => ({
          ...usuario,
          user_permissions: [...permissoesGrupos.user_permissions],
          groups: [...permissoesGrupos.groups],
        })),
        catchError(() => of(null)),
      );
    }),
  );
}

/** Localiza o id do usuário na API de autorização pelo login. */
function buscarUsuarioIdPorLogin(
  usuariosApi: UsuariosApiService,
  login: string,
): Observable<number | null> {
  return usuariosApi.usuarioList({ page: 1, pageSize: 500 }).pipe(
    map((resposta) => {
      const usuarios = resposta.results ?? [];
      const encontrado = usuarios.find(
        (usuario: ListRetrieveUsuario) =>
          usuario.login?.toLowerCase() === login.toLowerCase(),
      );
      return encontrado?.id ?? null;
    }),
    catchError(() => of(null)),
  );
}

export function hasUsuarioRelations(form: PessoaUsuarioFormState): boolean {
  return form.user_permissions.length > 0 || form.groups.length > 0;
}

export function mapPessoaComUsuarioToForm(pessoa: PessoaComUsuario): PessoaUsuarioFormState {
  const usuario = pessoa.usuario as ListRetrieveUsuario | undefined;

  return {
    nome: pessoa.nome,
    cpf: pessoa.cpf,
    login: usuario?.login ?? '',
    email: usuario?.email ?? '',
    password: '',
    is_active: usuario?.is_active ?? true,
    is_staff: usuario?.is_staff ?? false,
    user_permissions: [],
    groups: [],
    // Preview autenticado é carregado pelo componente pai (PessoaPhotoService).
    fotoUrl: null,
  };
}

export function normalizeCpf(value: string | undefined | null): string {
  return (value ?? '').replace(/\D/g, '');
}

export function formatCpfDisplay(value: string | undefined | null): string {
  if (!value || value === '—') {
    return value ?? '—';
  }

  return formatarCpf(value) || value;
}

export function buildPessoaUsuarioPayload(
  form: PessoaUsuarioFormState,
  includeUsuario: boolean,
): PessoaUsuarioFormPayload {
  const payload: PessoaUsuarioFormPayload = {
    nome: form.nome,
    cpf: normalizeCpf(form.cpf),
  };

  if (!includeUsuario) {
    return payload;
  }

  const usuario: PessoaUsuarioFormPayload['usuario'] = {
    login: form.login,
    email: form.email,
    is_active: form.is_active,
    is_staff: form.is_staff,
  };

  if (form.password?.trim()) {
    usuario.password = form.password;
  }

  payload.usuario = usuario;
  return payload;
}

export function hasUsuarioData(form: PessoaUsuarioFormState): boolean {
  return !!(form.login?.trim() || form.email?.trim() || form.password?.trim());
}

/** Na criação: se há dado de conta, exige login + e-mail + senha (tudo ou nada). */
export function validarContaUsuarioOpcional(
  form: PessoaUsuarioFormState,
  mode: 'create' | 'edit',
): string | null {
  if (mode !== 'create' || !hasUsuarioData(form)) {
    return null;
  }
  if (!form.login?.trim() || !form.email?.trim() || !form.password?.trim()) {
    return 'Para criar conta, preencha login, e-mail e senha.';
  }
  return null;
}
